import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { fetchDownlineUsers } from '../services/downlineApi';
import { canNavigateDown, getNextLevelUserType } from '../utils/downlineHelpers';
import { USER_LEVELS } from '../config/menuItems';
import UserExposureModal from '../components/UserExposureModal';
import { getAccessToken } from '../store/actions/auth';
import { resetPassword, toggleMatkaLock } from '../store/actions/userManagement';
import API_BASE_URL from '../config/baseUrl';
import { toast } from 'react-toastify';
import { formatDisplayUserId } from '../utils/userIdFormat';
import UserActionsDropdown from '../components/UserActionsDropdown';

// Icons (copied from UserListPage)
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M7 10l5 5 5-5z"/>
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.06-.05-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z"></path>
  </svg>
);

const UpIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3A8 8 0 00140 768h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path>
  </svg>
);

const DownIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path>
  </svg>
);

const EyeIcon = () => (
  <span className="ml-1">👁</span>
);

// Update Limit API function (same as UserListPage)
const updateLimit = async function(userId, limit, limitPlus) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/update-limit",
      {
        userId: userId,
        limit: limit,
        limitInCash: false,
        limitPlus: limitPlus,
      },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to update limit" };
    }

    return { success: true, message: data.message };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update limit";
    return { success: false, error: errorMessage };
  }
};

// Activate/Deactivate User API function
const activateDeactivateUser = async function(userId, activate) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/activate-deactivate-user",
      {
        activate: activate,
        userIdList: [userId],
      },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to update user status" };
    }

    return { success: true, message: data.message };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update user status";
    return { success: false, error: errorMessage };
  }
};

// Bet Lock API function
const toggleBetLockApi = async function(userId, isLock) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/bet-lock",
      {
        userId: userId,
        isLock: isLock,
      },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to update bet lock status" };
    }

    return { success: true, message: data.message };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update bet lock status";
    return { success: false, error: errorMessage };
  }
};

// Casino Bet Lock API function
const toggleCasinoLockApi = async function(userId, isLock) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/casino-bet-lock",
      {
        userId: userId,
        isLock: isLock,
      },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to update casino lock status" };
    }

    return { success: true, message: data.message };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update casino lock status";
    return { success: false, error: errorMessage };
  }
};

// Fetch parent share API
const fetchParentShare = async function(userId) {
  try {
    const token = getAccessToken();
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/get-parent-share",
      { userId: userId },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;
    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch data" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to fetch data" };
  }
};

// Partnership Modal Component
const PartnershipModal = ({ isOpen, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [parentShare, setParentShare] = useState(null);

  useEffect(function() {
    if (isOpen && userId) {
      setLoading(true);
      fetchParentShare(userId).then(function(result) {
        if (result.success) {
          setParentShare(result.data);
        }
        setLoading(false);
      });
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const renderTable = function (title, headers, data) {
    return (
      <div className="mb-6">
        <h3 className="font-semibold mb-2 text-base">{title}</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-[#2c3e50] text-white">
                {headers.map((header, index) => (
                  <th key={index} className="border border-gray-400 p-2 text-left">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                {data.map((value, index) => (
                  <td key={index} className="border border-gray-300 p-2">
                    {value}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getMatchShareData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0'];
    return [
      parentShare.adminpartnership || '0',
      parentShare.subadminpartnership || '0',
      parentShare.masterpartnership || '0',
      parentShare.supermasterpartnership || '0',
      parentShare.dealerpartnership || '0',
    ];
  };

  const getMatchCommData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0', '0'];
    return [
      parentShare.adminoddsloss || '0',
      parentShare.subadminoddsloss || '0',
      parentShare.masteroddsloss || '0',
      parentShare.supermasteroddsloss || '0',
      parentShare.dealeroddsloss || '0',
      '0',
    ];
  };

  const getSessionCommData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0', '0'];
    return [
      parentShare.adminfancyloss || '0',
      parentShare.subadminfancyloss || '0',
      parentShare.masterfancyloss || '0',
      parentShare.supermasterfancyloss || '0',
      parentShare.dealerfancyloss || '0',
      '0',
    ];
  };

  const getCasinoShareData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0'];
    return [
      parentShare.admincasinopartnership || '0',
      parentShare.subadmincasinopartnership || '0',
      parentShare.mastercasinopartnership || '0',
      parentShare.supermastercasinopartnership || '0',
      parentShare.dealercasinopartnership || '0',
    ];
  };

  const getCasinoCommData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0', '0'];
    return [
      parentShare.admincasinocommssion || '0',
      parentShare.subadmincasinocommssion || '0',
      parentShare.mastercasinocommssion || '0',
      parentShare.supermastercasinocommssion || '0',
      parentShare.dealercasinocommssion || '0',
      '0',
    ];
  };

  const getMatkaShareData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0'];
    return [
      parentShare.adminmatkapartnership || '0',
      parentShare.subadminmatkapartnership || '0',
      parentShare.mastermatkapartnership || '0',
      parentShare.supermastermatkapartnership || '0',
      parentShare.dealermatkapartnership || '0',
    ];
  };

  const getMatkaCommData = function () {
    if (!parentShare) return ['0', '0', '0', '0', '0', '0'];
    return [
      parentShare.adminmatkacommission || '0',
      parentShare.subadminmatkacommission || '0',
      parentShare.mastermatkacommission || '0',
      parentShare.supermastermatkacommission || '0',
      parentShare.dealermatkacommission || '0',
      '0',
    ];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-4 bg-[#2c3e50] text-white rounded-t-lg">
          <h3 className="text-lg font-normal">
            Partnership Info - {userId}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div>Loading...</div>
            </div>
          ) : parentShare ? (
            <>
              {renderTable("Match Share", ["S.Ad", "Mini", "Master", "Super", "Agent"], getMatchShareData())}
              {renderTable("Match Commission", ["S.Ad", "Mini", "Master", "Super", "Agent", "Client"], getMatchCommData())}
              {renderTable("Session Commission", ["S.Ad", "Mini", "Master", "Super", "Agent", "Client"], getSessionCommData())}
              {renderTable("Casino Share", ["S.Ad", "Mini", "Master", "Super", "Agent"], getCasinoShareData())}
              {renderTable("Casino Commission", ["S.Ad", "Mini", "Master", "Super", "Agent", "Client"], getCasinoCommData())}
              {renderTable("Matka Share", ["S.Ad", "Mini", "Master", "Super", "Agent"], getMatkaShareData())}
              {renderTable("Matka Commission", ["S.Ad", "Mini", "Master", "Super", "Agent", "Client"], getMatkaCommData())}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={onClose}
                  className="bg-[#2c3e50] text-white px-8 py-2 rounded hover:bg-[#34495e]"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 py-8">
              No partnership data found
            </p>
          )}
        </div>
      </div>
    </div>
  );
};


// Deposit/Withdraw Modal Component - Ant Design Style
const TransactionModal = ({ isOpen, onClose, type, user, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !user) return null;

  const isDeposit = type === 'deposit';
  const title = isDeposit ? 'Deposit' : 'Withdrawn';
  
  // Calculate current chips
  const currentChips = (user.balance || 0) + (user.balanceWithPnl || 0);

  const handleIncrement = function() {
    const currentValue = parseFloat(amount) || 0;
    setAmount(String(currentValue + 1));
  };

  const handleDecrement = function() {
    const currentValue = parseFloat(amount) || 0;
    if (currentValue > 0) {
      setAmount(String(currentValue - 1));
    }
  };

  const handleSubmit = async function() {
    const numAmount = parseFloat(amount);
    if (!numAmount || isNaN(numAmount) || numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);

    // For deposit: limitPlus = true, for withdraw: limitPlus = false
    const result = await updateLimit(user.userId, numAmount, isDeposit);

    if (result.success) {
      toast.success(result.message || (isDeposit ? 'Deposit successful!' : 'Withdrawal successful!'));
      setAmount('');
      onClose();
      if (onSuccess) onSuccess();
    } else {
      toast.error(result.error || 'Transaction failed');
    }

    setLoading(false);
  };

  const handleClose = function() {
    setAmount('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose}></div>
      <div 
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        style={{ boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)' }}
      >
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <CloseIcon />
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Current Coins Display */}
          <div className="text-xl text-gray-600 py-3">
            Curr Coins : {currentChips.toFixed(2)}
          </div>

          {/* Amount Input with Up/Down Buttons */}
          <div className="relative flex border border-gray-300 mb-1" style={{ borderRadius: 0 }}>
            <input
              type="number"
              value={amount}
              onChange={function(e) { setAmount(e.target.value); }}
              placeholder="Enter amount"
              className="flex-1 px-3 py-3 text-lg focus:outline-none"
              style={{ borderRadius: 0 }}
              step="1"
            />
            <div className="flex flex-col border-l border-gray-300">
              <button
                onClick={handleIncrement}
                className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-300"
              >
                <UpIcon />
              </button>
              <button
                onClick={handleDecrement}
                className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center"
              >
                <DownIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button 
            onClick={handleClose}
            className="px-4 py-1.5 bg-gray-500 text-white hover:bg-gray-600 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Return
          </button>
          <button 
            onClick={handleSubmit}
            disabled={loading || !amount}
            className="px-4 py-1.5 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300 transition-colors"
            style={{ borderRadius: 0 }}
          >
            {loading ? 'Processing...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Password Reset Modal
const PasswordModal = ({ isOpen, onClose, user, onSubmit, loading }) => {
  const [password, setPassword] = useState('');

  if (!isOpen) return null;

  const handleSubmit = function() {
    if (password) {
      onSubmit(password);
      setPassword('');
    }
  };

  const handleClose = function() {
    setPassword('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose}></div>
      <div 
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        style={{ boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)' }}
      >
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <CloseIcon />
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Reset Password - {formatDisplayUserId(user?.userId)}</h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <input
            type="text"
            value={password}
            onChange={function(e) { setPassword(e.target.value); }}
            placeholder="Enter new password"
            className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:border-blue-500"
            style={{ borderRadius: 0 }}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button 
            onClick={handleClose}
            className="px-4 py-1.5 bg-gray-500 text-white hover:bg-gray-600 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={loading || !password}
            className="px-4 py-1.5 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300 transition-colors"
            style={{ borderRadius: 0 }}
          >
            {loading ? 'Processing...' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DownlinePage = () => {
  const { parentId, parentUserType, parentName } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useSelector(state => state.auth);
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showExposureModal, setShowExposureModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [partnershipModal, setPartnershipModal] = useState({ isOpen: false, userId: null });
  const [searchFilters, setSearchFilters] = useState({ code: '', name: '', contact: '' });
  const [transactionModal, setTransactionModal] = useState({ isOpen: false, type: null, user: null });
  const [passwordModal, setPasswordModal] = useState({ isOpen: false, user: null });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const parentUserTypeNum = parseInt(parentUserType);
  const downlineUserType = getNextLevelUserType(parentUserTypeNum);
  const downlineUserTypeKeyMap = {
    [USER_LEVELS.SUPERADMIN]: 'superadmin',
    [USER_LEVELS.ADMIN]: 'admin',
    [USER_LEVELS.MINIADMIN]: 'mini-admin',
    [USER_LEVELS.MASTER]: 'master',
    [USER_LEVELS.SUPERMASTER]: 'super-master',
    [USER_LEVELS.AGENT]: 'agent',
    [USER_LEVELS.CLIENT]: 'client',
  };
  const downlineUserTypeKey = downlineUserTypeKeyMap[downlineUserType] || 'mini-admin';
  
  // Check if users at this level can navigate down further
  // If we're viewing clients (level 1), they can't navigate but should show exposure
  const isClientLevel = downlineUserType === USER_LEVELS.CLIENT;
  const hasLowerLevel = canNavigateDown(downlineUserType);
  // Make clickable if either has lower level OR is client level (for exposure modal)
  const isClickable = hasLowerLevel || isClientLevel;

  useEffect(() => {
    loadDownlineUsers();
  }, [parentId, currentPage]);

  const loadDownlineUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchDownlineUsers(
        downlineUserType,
        parentId,
        accessToken,
        250,
        currentPage,
        false
      );

      if (response.status && response.data) {
        setUsers(response.data.userListV2 || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading downline users');
      console.error('Downline load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    // If this is a Client (level 1), show exposure modal - no further navigation
    if (isClientLevel) {
      setSelectedUser(user);
      setShowExposureModal(true);
    } 
    // Otherwise, navigate to their downline
    else if (hasLowerLevel) {
      navigate(`/admin-details/downline/${user.userId}/${downlineUserType}/${encodeURIComponent(user.userName)}`);
    }
  };

  const handleExposureClick = function(user) {
    setSelectedUser(user);
    setShowExposureModal(true);
  };

  const handlePartnershipClick = function(userId) {
    setPartnershipModal({ isOpen: true, userId: userId });
  };

  const handleClosePartnership = function() {
    setPartnershipModal({ isOpen: false, userId: null });
  };

  const handleDropdownClick = function(e, userId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({ 
      top: rect.bottom + window.scrollY, 
      left: rect.left + window.scrollX 
    });
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  const handleCloseDropdown = function() {
    setActiveDropdown(null);
  };

  const handleAction = async function(action, userToUpdate) {
    setActiveDropdown(null);

    switch (action) {
      case 'deposit':
        setTransactionModal({ isOpen: true, type: 'deposit', user: userToUpdate });
        break;
      case 'withdraw':
        setTransactionModal({ isOpen: true, type: 'withdraw', user: userToUpdate });
        break;
      case 'toggleStatus':
        // activate: false to make inactive, activate: true to make active
        const statusResult = await activateDeactivateUser(userToUpdate.userId, !userToUpdate.isActive);
        if (statusResult.success) {
          toast.success(statusResult.message || 'User status updated successfully!');
          loadDownlineUsers();
        } else {
          toast.error(statusResult.error || 'Failed to update user status');
        }
        break;
      case 'toggleBetLock':
        // isLock: true to lock, isLock: false to unlock
        const betLockResult = await toggleBetLockApi(userToUpdate.userId, !userToUpdate.betLock);
        if (betLockResult.success) {
          toast.success(betLockResult.message || 'Bet lock status updated successfully!');
          loadDownlineUsers();
        } else {
          toast.error(betLockResult.error || 'Failed to update bet lock status');
        }
        break;
      case 'toggleCasinoLock':
        // isLock: true to lock, isLock: false to unlock
        const casinoLockResult = await toggleCasinoLockApi(userToUpdate.userId, !userToUpdate.casinoLock);
        if (casinoLockResult.success) {
          toast.success(casinoLockResult.message || 'Casino lock status updated successfully!');
          loadDownlineUsers();
        } else {
          toast.error(casinoLockResult.error || 'Failed to update casino lock status');
        }
        break;
      case 'toggleMatkaLock':
        // isLock: true to lock, isLock: false to unlock
        const matkaLockResult = await toggleMatkaLock(userToUpdate.userId, !userToUpdate.matkaBetLock);
        if (matkaLockResult.success) {
          toast.success(matkaLockResult.message || 'Matka lock status updated successfully!');
          loadDownlineUsers();
        } else {
          toast.error(matkaLockResult.error || 'Failed to update matka lock status');
        }
        break;
      case 'resetPassword':
        setPasswordModal({ isOpen: true, user: userToUpdate });
        break;
      case 'edit':
        navigate('/admin-details/edit/' + userToUpdate.userId);
        break;
      case 'statement':
        navigate('/admin-details/statement/' + userToUpdate.userId);
        break;
      case 'accountOps':
        const accountOpsUserId = userToUpdate.id || userToUpdate._id || userToUpdate.userId;
        navigate('/admin-details/account-operations/' + accountOpsUserId);
        break;
      case 'loginReport':
        navigate('/admin-details/login-report/' + userToUpdate.userId);
        break;
      case 'downline':
        handleUserClick(userToUpdate);
        break;
      default:
        break;
    }
  };

  const handleTransactionSuccess = function() {
    loadDownlineUsers();
  };

  const handlePasswordSubmit = async function(password) {
    setPasswordLoading(true);
    await resetPassword(passwordModal.user.userId, password);
    setPasswordLoading(false);
    setPasswordModal({ isOpen: false, user: null });
  };

  const formatDate = function(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return day + '-' + month + '-' + year;
  };

  const getCommissionType = function(type) {
    const types = {
      1: 'No Comm',
      2: 'BetByBet',
      3: 'BBB',
    };
    return types[type] || '-';
  };

  const formatUserId = function(userId) {
    return formatDisplayUserId(userId);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(function(user) {
    if (!user.isActive) return false;
    
    const codeMatch = !searchFilters.code || user.userId.toLowerCase().includes(searchFilters.code.toLowerCase());
    const nameMatch = !searchFilters.name || user.userName.toLowerCase().includes(searchFilters.name.toLowerCase());
    const contactMatch = !searchFilters.contact || (user.contact && user.contact.includes(searchFilters.contact));
    return codeMatch && nameMatch && contactMatch;
  });

  return (
    <div className="w-full">
      <div className="bg-white shadow">
        {/* Header - Exact copy from UserListPage */}
        <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal capitalize">
            {decodeURIComponent(parentName)}'s Downline
          </span>
          <button 
            onClick={function() { navigate(-1); }}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Action Buttons */}
        {downlineUserType && (
          <div className="px-3 py-3 flex justify-around gap-4">
            <Link 
              to={'/admin-details/create/' + downlineUserTypeKey}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Create
            </Link>
            <Link 
              to={'/admin-details/update-limit/' + downlineUserTypeKey}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Update Limit
            </Link>
          </div>
        )}

        {/* Table - With conditional Exposure column for client level */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 text-center">{error}</div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white border-y">
                  <th className="p-2 border text-left">#</th>
                  <th className="p-2 border text-left"></th>
                  <th className="p-2 border text-left">
                    <div className="flex items-center gap-1">
                      Code
                      <SearchIcon />
                    </div>
                  </th>
                  <th className="p-2 border text-left">
                    <div className="flex items-center gap-1">
                      Name
                      <SearchIcon />
                    </div>
                  </th>
                  <th className="p-2 border text-left">
                    <div className="flex items-center gap-1">
                      <span className="capitalize">Parent</span>
                      <SearchIcon />
                    </div>
                  </th>
                  <th className="p-2 border text-left">
                    <div className="flex items-center gap-1">
                      Contact
                      <SearchIcon />
                    </div>
                  </th>
                  <th className="p-2 border text-left">D.O.J</th>
                  <th className="p-2 border text-left">Share %</th>
                  <th className="p-2 border text-center" colSpan="3">Master Comm %</th>
                  <th className="p-2 border text-left">My.Chip</th>
                  {isClientLevel && (
                    <th className="p-2 border text-left">Exposure</th>
                  )}
                  <th className="p-2 border text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={isClientLevel ? "14" : "13"} className="p-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(function(user, index) {
                    const rowBgColor = index % 2 === 0 ? 'var(--row-even)' : "var(--row-odd)";
                    return (
                      <tr key={user.userId} style={{ backgroundColor: rowBgColor }}>
                        <td className="p-2 border">
                          <button
                            onClick={function() { handlePartnershipClick(user.userId); }}
                            className="bg-blue-500 text-white p-1 rounded"
                            title="View Partnership"
                          >
                            <PlusIcon />
                          </button>
                        </td>
                        <td className="p-2 border">
                          <button
                            onClick={function(e) { handleDropdownClick(e, user.userId); }}
                            className="bg-blue-500 text-white p-1 rounded"
                          >
                            <MenuIcon />
                          </button>
                        </td>
                        <td className="p-2 border">
                          <span>
                            {formatUserId(user.userId)}
                            <span
                              className={isClickable ? "cursor-pointer whitespace-nowrap text-blue-600 hover:text-blue-800" : "whitespace-nowrap"}
                              onClick={isClickable ? function() { handleUserClick(user); } : undefined}
                            >
                              {hasLowerLevel && <EyeIcon />}
                            </span>
                          </span>
                        </td>
                        <td className="p-2 border">
                          <span 
                            className={isClickable ? "cursor-pointer whitespace-nowrap text-blue-600 hover:text-blue-800" : "whitespace-nowrap"}
                            onClick={isClickable ? function() { handleUserClick(user); } : undefined}
                          >
                            {user.userName}
                          </span>
                        </td>
                        <td className="p-2 border">{user.parentName} ({formatUserId(user.parentId)})</td>
                        <td className="p-2 border">{user.contact}</td>
                        <td className="p-2 border">
                          <span className="whitespace-nowrap">{formatDate(user.createdOn)}</span>
                        </td>
                        <td className="p-2 border">{user.partnerShip}</td>
                        <td className="p-2 border">{getCommissionType(user.commisstionType)}</td>
                        <td className="p-2 border">{user.matchCommission}</td>
                        <td className="p-2 border">{user.sessionCommission}</td>
                        <td className="p-2 border">{((user.balance || 0) + (user.balanceWithPnl || 0)).toFixed(2)}</td>
                        {isClientLevel && (
                          <td className="p-2 border">
                            <button
                              onClick={function() { handleExposureClick(user); }}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {(user.liability || 0).toFixed(2)}
                            </button>
                          </td>
                        )}
                        <td className="p-2 border">
                          <span className={user.isActive}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination - Exact copy from UserListPage */}
        <div className="flex justify-end p-3 gap-1">
          <button 
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage === 0}
            onClick={function() { handlePageChange(currentPage - 1); }}
          >
            &lt;
          </button>
          <button className="px-3 py-1 bg-blue-500 text-white rounded">
            {currentPage + 1}
          </button>
          <button 
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage >= totalPages - 1}
            onClick={function() { handlePageChange(currentPage + 1); }}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Dropdown Menu */}
      {activeDropdown && (
        <UserActionsDropdown
          user={users.find(function(u) { return u.userId === activeDropdown; })}
          onAction={handleAction}
          onClose={handleCloseDropdown}
          position={dropdownPosition}
          backdrop={true}
          backdropClassName="fixed inset-0 z-40"
          menuClassName="absolute z-50 bg-white border border-gray-300 shadow-lg rounded py-1 min-w-[180px] list-none m-0 p-0 max-h-[70vh] overflow-y-auto"
          menuStyle={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        />
      )}

      {/* Partnership Modal */}
      <PartnershipModal
        isOpen={partnershipModal.isOpen}
        onClose={handleClosePartnership}
        userId={partnershipModal.userId}
      />

      {/* User Exposure Modal */}
      <UserExposureModal
        isOpen={showExposureModal}
        onClose={() => setShowExposureModal(false)}
        userId={selectedUser?.userId}
        userName={selectedUser?.userName}
      />

      <TransactionModal
        isOpen={transactionModal.isOpen}
        type={transactionModal.type}
        user={transactionModal.user}
        onClose={function() { setTransactionModal({ isOpen: false, type: null, user: null }); }}
        onSuccess={handleTransactionSuccess}
      />

      <PasswordModal
        isOpen={passwordModal.isOpen}
        user={passwordModal.user}
        onClose={function() { setPasswordModal({ isOpen: false, user: null }); }}
        onSubmit={handlePasswordSubmit}
        loading={passwordLoading}
      />
    </div>
  );
};

export default DownlinePage;
